import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import NoData from "components/common/NoData";
import LoadingSpinner from "components/common/LoadingSpiner";
import LoginBottomSheet from "components/common/LoginBottomSheet";
import TourCard from "components/tour/TourCard";
import useUserStore from "@store/useUserStore";
import useTourLikes from "hooks/useTourLikes";
import { getMyLikedTravelProducts } from "api/travel";

// 찜 목록 아이템이 투어 객체 자체이거나 { travel_product: {...} } 로 감싸져 와도 투어 객체로 맞춘다.
const toTour = (item) => item?.travel_product ?? item?.product ?? item;

const TourLikes = () => {
    const navigate = useNavigate();
    const userInfo = useUserStore((state) => state.userInfo);
    const { isLiked, toggleLike } = useTourLikes();

    const [tours, setTours] = useState([]);
    const [page, setPage] = useState(1);
    const [hasNext, setHasNext] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isError, setIsError] = useState(false);
    const [showLoginBottomSheet, setShowLoginBottomSheet] = useState(false);
    const sentinelRef = useRef(null);

    const fetchPage = useCallback(async (pageNum) => {
        const res = await getMyLikedTravelProducts(pageNum);
        const list = Array.isArray(res) ? res : res?.results || [];
        return {
            items: list.map(toTour).filter(Boolean),
            next: Array.isArray(res) ? false : Boolean(res?.next),
        };
    }, []);

    const fetchInitial = useCallback(async () => {
        setIsLoading(true);
        setIsError(false);
        try {
            const { items, next } = await fetchPage(1);
            setTours(items);
            setHasNext(next);
            setPage(1);
        } catch (err) {
            console.error("getMyLikedTravelProducts API 에러:", err);
            setIsError(true);
        } finally {
            setIsLoading(false);
        }
    }, [fetchPage]);

    const loadMore = useCallback(async () => {
        if (!hasNext || isLoadingMore) return;
        setIsLoadingMore(true);
        try {
            const nextPage = page + 1;
            const { items, next } = await fetchPage(nextPage);
            setTours((prev) => [...prev, ...items]);
            setHasNext(next);
            setPage(nextPage);
        } catch (err) {
            console.error("getMyLikedTravelProducts API 에러:", err);
            setHasNext(false);
        } finally {
            setIsLoadingMore(false);
        }
    }, [fetchPage, hasNext, isLoadingMore, page]);

    useEffect(() => {
        if (userInfo) fetchInitial();
        else setIsLoading(false);
    }, [userInfo, fetchInitial]);

    useEffect(() => {
        const node = sentinelRef.current;
        if (!node || !hasNext) return undefined;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) loadMore();
            },
            { rootMargin: "200px 0px" }
        );
        observer.observe(node);
        return () => observer.disconnect();
    }, [hasNext, loadMore]);

    // 찜 해제해도 바로 목록에서 빼지 않는다 (실수로 누른 경우 다시 찜할 수 있도록). 재진입 시 반영.
    const handleLikeClick = async (id) => {
        const handled = await toggleLike(id);
        if (!handled) setShowLoginBottomSheet(true);
    };

    const renderBody = () => {
        if (!userInfo) {
            return (
                <div className="no-data-wrap">
                    <NoData title="로그인이 필요해요." desc="로그인하고 마음에 드는 투어를 찜해보세요." />
                    <button
                        type="button"
                        className="btn btn-purple500 tour-likes-cta"
                        onClick={() => setShowLoginBottomSheet(true)}
                    >
                        로그인
                    </button>
                </div>
            );
        }
        if (isLoading) {
            return (
                <div className="tour-loading-more tour-likes-loading">
                    <LoadingSpinner />
                </div>
            );
        }
        if (isError) {
            return (
                <div className="no-data-wrap">
                    <NoData title="찜한 투어를 불러오지 못했어요." desc="잠시 후 다시 시도해주세요." />
                    <button type="button" className="btn btn-purple500 tour-likes-cta" onClick={fetchInitial}>
                        다시 시도
                    </button>
                </div>
            );
        }
        if (tours.length === 0) {
            return (
                <div className="no-data-wrap">
                    <NoData title="아직 찜한 투어가 없어요." desc="마음에 드는 투어의 하트를 눌러보세요." />
                    <button type="button" className="btn btn-purple500 tour-likes-cta" onClick={() => navigate("/tour/list")}>
                        투어 둘러보기
                    </button>
                </div>
            );
        }
        return (
            <>
                <div className="package-grid">
                    {tours.map((t) => (
                        <TourCard
                            key={`tour-like-${t.id}`}
                            tour={t}
                            onClick={(id) => navigate(`/tour/detail/${id}`)}
                            liked={isLiked(t.id)}
                            onLikeClick={handleLikeClick}
                        />
                    ))}
                </div>
                <div ref={sentinelRef} style={{ height: 1 }} />
                {isLoadingMore && (
                    <div className="tour-loading-more">
                        <LoadingSpinner />
                    </div>
                )}
            </>
        );
    };

    return (
        <div className="tour-page tour-likes-page">
            <SubHeader title="찜한 투어" />
            {renderBody()}
            <LoginBottomSheet
                isOpen={showLoginBottomSheet}
                onClose={() => setShowLoginBottomSheet(false)}
            />
        </div>
    );
};

export default TourLikes;
